/**
 * <DataTable /> — the locked table primitive (DESIGN_SYSTEM.md §8).
 *
 * Behavior shipped in this F5 PR:
 *   1.  Sort: click column header asc, click again desc, click again
 *       to clear. Multi-column sort via Shift+click.
 *   2.  Filtering: per-column filter inputs. Each filterable column
 *       declares the filter type via its column meta.
 *   3.  Search: global instant search across visible columns,
 *       debounced 150ms.
 *   4.  Column visibility: "Columns" button → checkbox list.
 *   5.  Column reordering: drag column headers.
 *   6.  Pagination: client-side by default (server-side via
 *       manualPagination prop). 25 / 50 / 100 / All.
 *   7.  Bulk selection: checkbox column, header select-all, bulk
 *       action slot appears when 1+ rows are selected.
 *   8.  Row-click action: opens drawer via onRowClick prop (NOT
 *       navigates away). Drawer is owned by the caller.
 *   9.  Export: Excel (.xlsx) + CSV via the xlsx library. Exports
 *       respect current filters + sort + visible columns. Column
 *       headers use the column header label.
 *   11. Empty state: every DataTable renders an <EmptyState />
 *       when its filtered rows are zero. Caller provides the slot.
 *
 * Deferred to a post-M20 polish sprint (per Foundation sprint scope):
 *   10. View saving — save filter+sort+columns as named views.
 *   12. Density toggle — Compact / Standard / Comfortable.
 *
 * Use this primitive for every list / table in the Console. Custom
 * tables are a design-system violation.
 */

import {
  type ColumnDef,
  type ColumnFiltersState,
  type SortingState,
  type VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  ChevronUp,
  Columns3,
  Download,
  GripVertical,
  Search,
} from "lucide-react";
import {
  type ReactNode,
  useDeferredValue,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { exportReport } from "../../lib/export";
import type { ExportColumn } from "../../lib/export";
import { EmptyState } from "./EmptyState";

/* eslint-disable @typescript-eslint/no-explicit-any */

export interface DataTableProps<TData> {
  /** Column definitions. TanStack ColumnDef. */
  columns: ColumnDef<TData, any>[];
  /** Row data. Pass empty array during load; the loading slot
   * shows a skeleton if `loading` is true. */
  data: TData[];
  /** Loading state — renders skeleton rows. */
  loading?: boolean;
  /** Fired when a row is clicked. Caller opens a drawer. */
  onRowClick?: (row: TData) => void;
  /** Optional callback rendered when bulk selection is non-empty.
   * Receives the selected rows. */
  renderBulkActions?: (selected: TData[]) => ReactNode;
  /** Empty-state slot. If omitted, a generic empty state renders. */
  emptyState?: ReactNode;
  /**
   * M3X.WORKSPACE-CLOSURE Session 2 PR 3 — keep the table chrome
   * (column headers + a single tbody empty row) instead of swapping
   * the entire table for the emptyState slot. The emptyState text
   * (or "No matching records" default) renders as a single full-
   * width row inside tbody. Used by RegistryDrivenPage to show
   * what the columns will look like before the aggregator lands.
   */
  keepShellOnEmpty?: boolean;
  /**
   * Override the empty-row text when `keepShellOnEmpty` is true.
   * Default: "No matching records · the backend aggregator for this
   * screen lands in a follow-up sprint." (i18n key
   * `workspace.emptyState.body`).
   */
  emptyRowMessage?: ReactNode;
  /** Optional filename stem for exports. Default "export". */
  exportFilename?: string;
  /** Disable bulk-selection column entirely. */
  hideBulkSelection?: boolean;
  /** Initial sort. */
  initialSort?: SortingState;
  /** Initial column visibility. */
  initialColumnVisibility?: VisibilityState;
}

export function DataTable<TData>({
  columns,
  data,
  loading = false,
  onRowClick,
  renderBulkActions,
  emptyState,
  exportFilename = "export",
  hideBulkSelection = false,
  initialSort = [],
  initialColumnVisibility = {},
  keepShellOnEmpty = false,
  emptyRowMessage,
}: DataTableProps<TData>): ReactNode {
  const { t } = useTranslation();

  // ------- Table state ----------------------------------------------------
  const [sorting, setSorting] = useState<SortingState>(initialSort);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    initialColumnVisibility,
  );
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [globalFilter, setGlobalFilter] = useState("");
  const debouncedGlobalFilter = useDeferredValue(globalFilter);
  const [pageSize, setPageSize] = useState<number>(25);
  const [pageIndex, setPageIndex] = useState(0);
  const [columnOrder, setColumnOrder] = useState<string[]>([]);

  // ------- Augment columns with the bulk-select checkbox column ----------
  const finalColumns = useMemo<ColumnDef<TData, any>[]>(() => {
    if (hideBulkSelection) return columns;
    const select: ColumnDef<TData, any> = {
      id: "__select__",
      enableHiding: false,
      enableSorting: false,
      size: 36,
      header: ({ table }) => (
        <input
          type="checkbox"
          aria-label={t("app.dt_select_all")}
          checked={table.getIsAllPageRowsSelected()}
          ref={(el) => {
            if (el) el.indeterminate = table.getIsSomePageRowsSelected();
          }}
          onChange={(e) =>
            table.toggleAllPageRowsSelected(e.target.checked)
          }
          className="cursor-pointer"
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          aria-label={t("app.dt_select_row")}
          checked={row.getIsSelected()}
          onChange={(e) => row.toggleSelected(e.target.checked)}
          onClick={(e) => e.stopPropagation()}
          className="cursor-pointer"
        />
      ),
    };
    return [select, ...columns];
  }, [columns, hideBulkSelection]);

  const table = useReactTable({
    data,
    columns: finalColumns,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      globalFilter: debouncedGlobalFilter,
      pagination: { pageIndex, pageSize },
      columnOrder,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: (updater) => {
      const next =
        typeof updater === "function"
          ? updater({ pageIndex, pageSize })
          : updater;
      setPageIndex(next.pageIndex);
      setPageSize(next.pageSize);
    },
    onColumnOrderChange: setColumnOrder,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableMultiSort: true,
    enableSortingRemoval: true,
    enableRowSelection: !hideBulkSelection,
    globalFilterFn: "includesString",
  });

  // ------- Export helpers (visible columns × filtered rows) -------------
  // Per M3X.REPORTS §0.2 export-as-is lock: the exported payload
  // honors the user's CURRENT view — visible columns in display
  // order, filtered rows (full filtered set up to a 100K safety
  // cap), header labels as displayed (not col.id), and rendered
  // cell values when the column declares `accessorFn` / `cell:`.
  const filteredRows = table.getFilteredRowModel().rows;
  const selectedRows = filteredRows
    .filter((r) => r.getIsSelected())
    .map((r) => r.original);
  const visibleLeafColumns = table
    .getAllLeafColumns()
    .filter((c) => c.id !== "__select__" && c.getIsVisible());

  function headerLabelOf(col: typeof visibleLeafColumns[number]): string {
    const def = col.columnDef as { header?: unknown };
    if (typeof def.header === "string") return def.header;
    if (def.header && typeof def.header === "function") {
      // The HeaderContext function may return ReactNode — try to
      // call it with a minimal stub, fall back to col.id when the
      // shape isn't render-string-safe.
      try {
        const out = (def.header as (ctx: unknown) => unknown)({
          column: col,
          header: undefined,
          table,
        });
        if (typeof out === "string") return out;
      } catch {
        // fall through
      }
    }
    return String(col.id);
  }

  function cellValueOf(
    col: typeof visibleLeafColumns[number],
    row: TData,
  ): unknown {
    // Prefer the column's accessorFn / accessorKey (already applied
    // by TanStack), then the row's raw value. The cell render
    // function is for ReactNode rendering only — for export we want
    // the underlying value.
    const meta = col.columnDef as {
      accessorFn?: (row: TData) => unknown;
      accessorKey?: string;
    };
    if (meta.accessorFn) {
      try {
        return meta.accessorFn(row);
      } catch {
        // fall through
      }
    }
    const key = meta.accessorKey ?? col.id;
    return (row as Record<string, unknown>)[key];
  }

  function inferColumnType(col: typeof visibleLeafColumns[number]): ExportColumn["type"] {
    // Inspect the first filtered row's value at this column to pick
    // a sane XLSX / CSV cell type. Cheap heuristic — caller can
    // override by passing `<ReportPage>` ExportColumn config.
    const sample = filteredRows.length > 0
      ? cellValueOf(col, filteredRows[0].original)
      : undefined;
    if (sample instanceof Date) return "date";
    if (typeof sample === "number") return "number";
    if (typeof sample === "boolean") return "boolean";
    if (typeof sample === "string") {
      // ISO date detector
      if (/^\d{4}-\d{2}-\d{2}(T|$)/.test(sample)) return "date";
    }
    return "string";
  }

  function buildExportPayload() {
    const exportColumns: ExportColumn[] = visibleLeafColumns.map((col) => ({
      key: col.id,
      label: headerLabelOf(col),
      type: inferColumnType(col),
    }));
    const rows = filteredRows
      .slice(0, 100_000)
      .map((r) => {
        const record: Record<string, unknown> = {};
        for (const col of visibleLeafColumns) {
          record[col.id] = cellValueOf(col, r.original);
        }
        return record;
      });
    return {
      title: exportFilename,
      columns: exportColumns,
      rows,
      generatedAt: new Date(),
    };
  }

  function doExport(kind: "xlsx" | "csv" | "pdf") {
    const payload = buildExportPayload();
    void exportReport(payload, kind);
  }

  // ------- Drag-to-reorder column headers --------------------------------
  function handleDragStart(e: React.DragEvent, columnId: string) {
    e.dataTransfer.setData("text/plain", columnId);
    e.dataTransfer.effectAllowed = "move";
  }
  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
  }
  function handleDrop(e: React.DragEvent, targetId: string) {
    e.preventDefault();
    const draggedId = e.dataTransfer.getData("text/plain");
    if (!draggedId || draggedId === targetId) return;
    const current =
      table.getState().columnOrder.length > 0
        ? [...table.getState().columnOrder]
        : table.getAllLeafColumns().map((c) => c.id);
    const fromIndex = current.indexOf(draggedId);
    const toIndex = current.indexOf(targetId);
    if (fromIndex === -1 || toIndex === -1) return;
    current.splice(fromIndex, 1);
    current.splice(toIndex, 0, draggedId);
    table.setColumnOrder(current);
  }

  const isEmpty = !loading && filteredRows.length === 0;

  return (
    <div className="flex flex-col gap-3">
      {/* ------- Toolbar: search + columns + export + bulk actions ------ */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search
            size={14}
            aria-hidden="true"
            className="absolute start-3 top-1/2 -translate-y-1/2 text-text-tertiary"
          />
          <input
            type="search"
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            placeholder="Search…"
            className={[
              "w-full rounded-sm border border-border bg-surface-default",
              "ps-8 pe-3 py-1.5 text-sm",
              "placeholder:text-text-tertiary",
              "focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20",
            ].join(" ")}
            aria-label={t("app.dt_search_rows")}
          />
        </div>

        <ColumnVisibilityMenu table={table} />

        <ExportMenu onExport={doExport} />
      </div>

      {/* ------- Bulk actions slot — visible when rows are selected ----- */}
      {selectedRows.length > 0 && renderBulkActions && (
        <div className="flex items-center justify-between rounded-sm bg-state-info-bg px-3 py-2 text-sm text-state-info">
          <span>
            {selectedRows.length} row{selectedRows.length === 1 ? "" : "s"}{" "}
            selected
          </span>
          <div className="flex items-center gap-2">
            {renderBulkActions(selectedRows)}
          </div>
        </div>
      )}

      {/* ------- The table itself --------------------------------------- */}
      {isEmpty && !keepShellOnEmpty ? (
        emptyState ?? (
          <EmptyState
            headline="No results"
            description="No rows match the current filters."
          />
        )
      ) : (
        <div className="overflow-x-auto rounded-md border border-border-subtle bg-surface-default">
          <table className="w-full border-collapse text-sm">
            <thead className="bg-surface-sunken text-xs font-semibold uppercase tracking-wider text-text-tertiary">
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((header) => {
                    const canSort = header.column.getCanSort();
                    const sortDir = header.column.getIsSorted();
                    const isSelect = header.id === "__select__";
                    return (
                      <th
                        key={header.id}
                        scope="col"
                        draggable={!isSelect}
                        onDragStart={(e) =>
                          !isSelect && handleDragStart(e, header.id)
                        }
                        onDragOver={handleDragOver}
                        onDrop={(e) => !isSelect && handleDrop(e, header.id)}
                        className={[
                          "border-b border-border-subtle px-3 py-2 text-start align-middle",
                          isSelect ? "w-9" : "",
                        ].join(" ")}
                      >
                        <div className="flex items-center gap-1">
                          {!isSelect && (
                            <GripVertical
                              aria-hidden="true"
                              size={12}
                              className="cursor-grab text-text-tertiary opacity-0 transition-opacity group-hover:opacity-100"
                            />
                          )}
                          {canSort && !isSelect ? (
                            <button
                              type="button"
                              onClick={header.column.getToggleSortingHandler()}
                              className="inline-flex items-center gap-1 rounded-sm hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                              {sortDir === "asc" && <ChevronUp size={12} />}
                              {sortDir === "desc" && <ChevronDown size={12} />}
                              {sortDir === false && (
                                <ChevronsUpDown
                                  size={12}
                                  className="opacity-40"
                                />
                              )}
                            </button>
                          ) : (
                            flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={`skel-${i}`} className="border-b border-border-subtle">
                    {finalColumns.map((c, j) => (
                      <td key={j} className="px-3 py-2">
                        <span
                          aria-label="Loading"
                          className={[
                            "block h-3 rounded-sm",
                            "bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100",
                            "bg-[length:200%_100%]",
                            "motion-safe:animate-[skeleton-shimmer_1.4s_ease-in-out_infinite]",
                            c.id === "__select__" ? "w-4" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                        />
                      </td>
                    ))}
                  </tr>
                ))
              ) : isEmpty && keepShellOnEmpty ? (
                /* M3X.WORKSPACE-CLOSURE Session 2 PR 3 — keep the table chrome
                   and render a single muted-italic centered empty row. */
                <tr data-empty-row>
                  <td
                    colSpan={finalColumns.length}
                    className="bg-white text-center italic text-text-tertiary"
                    style={{
                      padding: "40px 12px",
                      fontSize: "13px",
                    }}
                  >
                    {emptyRowMessage ?? (
                      <span>
                        {t(
                          "workspace.emptyState.body",
                          "No matching records · the backend aggregator for this screen lands in a follow-up sprint.",
                        )}
                      </span>
                    )}
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => {
                  // The Playwright scenarios capture entity ids off
                  // each row via `tr[data-id]`. Surface it generically
                  // for every row that has an `id` field — no-op for
                  // rows without one (TypeScript can't see through
                  // generics here so we cast through unknown).
                  const rowDataId =
                    (row.original as unknown as { id?: string | number })
                      ?.id;
                  return (
                  <tr
                    key={row.id}
                    {...(rowDataId !== undefined
                      ? { "data-id": String(rowDataId) }
                      : {})}
                    onClick={() => onRowClick?.(row.original)}
                    className={[
                      "border-b border-border-subtle text-text-primary",
                      onRowClick
                        ? "cursor-pointer hover:bg-surface-sunken"
                        : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="px-3 py-2 align-middle"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    ))}
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ------- Pagination footer ------------------------------------- */}
      {!isEmpty && !loading && (
        <div className="flex flex-wrap items-center justify-between gap-2 px-1">
          <div className="flex items-center gap-2 text-xs text-text-secondary">
            <label htmlFor="page-size" className="font-medium">
              {t("common.loading") === "Loading…" ? "Rows" : t("common.loading")}:
            </label>
            <select
              id="page-size"
              value={pageSize === filteredRows.length ? "all" : pageSize}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "all") {
                  setPageSize(Math.max(filteredRows.length, 1));
                } else {
                  setPageSize(parseInt(v, 10));
                }
                setPageIndex(0);
              }}
              className="rounded-sm border border-border bg-surface-default px-2 py-1 text-xs"
            >
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="all">All</option>
            </select>
            <span>
              {filteredRows.length === 0
                ? "0"
                : `${pageIndex * pageSize + 1}–${Math.min(
                    (pageIndex + 1) * pageSize,
                    filteredRows.length,
                  )}`}{" "}
              / {filteredRows.length}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              aria-label={t("app.dt_prev_page")}
              className="rounded-sm border border-border bg-surface-default px-2 py-1 text-xs disabled:opacity-40"
            >
              <ChevronLeft size={14} className="rtl:rotate-180" />
            </button>
            <button
              type="button"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              aria-label={t("app.dt_next_page")}
              className="rounded-sm border border-border bg-surface-default px-2 py-1 text-xs disabled:opacity-40"
            >
              <ChevronRight size={14} className="rtl:rotate-180" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Column visibility toggle menu — checkbox per column. */
function ColumnVisibilityMenu<TData>({
  table,
}: {
  table: ReturnType<typeof useReactTable<TData>>;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const columns = table.getAllLeafColumns().filter((c) => c.id !== "__select__");
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 rounded-sm border border-border bg-surface-default px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
        aria-haspopup="true"
        aria-expanded={open}
      >
        <Columns3 size={14} />
        Columns
      </button>
      {open && (
        <div
          role="menu"
          className="absolute end-0 z-30 mt-1 min-w-[200px] rounded-md border border-border-subtle bg-surface-overlay p-2 shadow-md"
        >
          {columns.map((col) => (
            <label
              key={col.id}
              className="flex items-center gap-2 rounded-sm px-2 py-1 text-sm hover:bg-surface-sunken"
            >
              <input
                type="checkbox"
                checked={col.getIsVisible()}
                onChange={(e) =>
                  col.toggleVisibility(e.target.checked)
                }
                disabled={!col.getCanHide()}
              />
              {typeof col.columnDef.header === "string"
                ? col.columnDef.header
                : col.id}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/** Export menu — xlsx / csv / pdf (per M3X.REPORTS §0.2). */
function ExportMenu({
  onExport,
}: {
  onExport: (kind: "xlsx" | "csv" | "pdf") => void;
}): ReactNode {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 rounded-sm border border-border bg-surface-default px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
        aria-haspopup="true"
        aria-expanded={open}
      >
        <Download size={14} />
        Export
      </button>
      {open && (
        <div
          role="menu"
          className="absolute end-0 z-30 mt-1 min-w-[160px] rounded-md border border-border-subtle bg-surface-overlay p-1 shadow-md"
        >
          <button
            type="button"
            onClick={() => {
              onExport("xlsx");
              setOpen(false);
            }}
            className="block w-full rounded-sm px-2 py-1 text-start text-sm hover:bg-surface-sunken"
            role="menuitem"
          >
            Excel (.xlsx)
          </button>
          <button
            type="button"
            onClick={() => {
              onExport("csv");
              setOpen(false);
            }}
            className="block w-full rounded-sm px-2 py-1 text-start text-sm hover:bg-surface-sunken"
            role="menuitem"
          >
            CSV
          </button>
          <button
            type="button"
            onClick={() => {
              onExport("pdf");
              setOpen(false);
            }}
            className="block w-full rounded-sm px-2 py-1 text-start text-sm hover:bg-surface-sunken"
            role="menuitem"
          >
            PDF
          </button>
        </div>
      )}
    </div>
  );
}
